import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { ViewCompanyComponent } from 'components/company/viewcompany.component';
import { NotificationPopupComponent } from 'components/core/notification-popup/notification-popup.component';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { DateInputComponent } from 'components/input/date-input/date-input.component';
import { UploadedFile } from 'components/input/file-upload/file-upload';
import { SubmitButtonComponent } from 'components/input/submit-button/submit-button.component';
import { InputStateDirective } from 'directives/input-state.directive';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { VariantListTypes } from 'enums/VariantListTypes';
import { OrderLineModel } from 'models/generated/Entities.GormCrm.OrderLine';
import { ProductModel } from 'models/generated/Entities.GormCrm.Product';
import { ProductTagFilterModel } from 'models/generated/Entities.GormCrm.ProductTagFilter';
import { ProductVariantModel } from 'models/generated/Entities.GormCrm.ProductVariant';
import { SaleAgreementModel } from 'models/generated/Entities.GormCrm.SaleAgreement';
import { VariantListModel } from 'models/generated/Entities.GormCrm.VariantList';
import { VariantListOptionModel } from 'models/generated/Entities.GormCrm.VariantListOption';
import { SelectListItemModel } from 'models/SelectList';
import { Layout } from 'modules/layout';
import { CurrencyPipe } from 'pipes/currency.pipe';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { FormValidationService } from 'services/form-validation.service';
import { ComponentUrl, NavigationService, UrlParam } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { ProductService } from 'services/product.service';
import { SalesService } from 'services/sales.service';
import { TaskService } from 'services/task.service';
import { UserService } from 'services/user.service';

@Component({
	selector: 'editsale',
	templateUrl: './editsale.component.html',
	styleUrls: ['./editsale.component.scss'],
	providers: [FormValidationService],
	imports: [Layout, NgClass, CustomInputComponent, CustomSelectComponent, NgTemplateOutlet, DateInputComponent, InputStateDirective, CurrencyPipe, TranslatePipe]
})
@ComponentUrl('editsale')
export class EditSaleComponent {
	@ViewChild('createSaleButton') createSaleButton!: SubmitButtonComponent;
	@ViewChild('basket') basketArea?: ElementRef<HTMLElement>;

	selectedFilter: ProductTagFilterModel | null = null;
	filters: ProductTagFilterModel[] = [];
	products: ProductModel[] = [];
	variantLists: VariantListModel[] = [];
	activeProduct: ProductModel | null = null;
	selectedVariantListOptions: VariantListOptionModel[] = [];
	price: number = 0;
	monthPrice: number = 0;
	discount: string = '0';
	selectedVariant: ProductVariantModel | null = null;
	selectedVariantPeriod: number = 0;
	saleAgreements: SaleAgreementModel[] = [];
	selectedAgreement?: SaleAgreementModel;
	inputType = InputType;
	loaded = false;
	orderLines: OrderLineModel[] = [];
	parentOrderLines: OrderLineModel[] = [];
	companyProducts: OrderLineModel[] = [];
	parentCompanyProducts: OrderLineModel[] = [];
	orderLineTotal: number = 0;
	uploadedFiles: UploadedFile[] = [];
	signDate?: Date;
	pickingParentProduct?: { pendingOrderLine: OrderLineModel, targetProduct: ProductModel };
	credentials: Map<string, CredentialType> = new Map();
	userCredentials: CredentialType[] = [];
	uploading = false;
	saving = false;
	orderId?: string;

	quantityList: SelectListItemModel[] = [];
	minQuantity = 1;
	maxQuantity = 20;

	private quantitySelected = 1;
	public set quantitySelectedAsString(value: string | null) {
		if (!value) return;
		const number = parseInt(value);
		if (isNaN(number)) return;
		this.quantitySelected = parseInt(value);
	}
	public get quantitySelectedAsString() { return this.quantitySelected.toString(); }

	private _taskflowId?: string;
	set taskflowId(taskFlowId: string | undefined) {
		if (taskFlowId) {
			this._taskflowId = taskFlowId;
			this.taskService.getTaskflow(taskFlowId).then(taskflow => {
				if (this.companyId !== taskflow.companyId!)
					this.companyId = taskflow.companyId!;
			});
		}
	}
	get taskflowId() {
		return this._taskflowId;
	}

	private _companyId?: string;
	@UrlParam
	set companyId(companyId: string) {
		this.navigation.refreshUrl();
		if (companyId) {
			this._companyId = companyId;
			this.getSaleAgreements();
			this.navigation.openNavigationView(this, 'company-related-views', ViewCompanyComponent)
				.then(viewCompany => {
					viewCompany.companyId = companyId;
				});
			this.salesService.getCompanyProducts(companyId).then(orderLines => {
				this.companyProducts = orderLines;
				this.parentCompanyProducts = orderLines.filter(product => !product.parentLine);
				this.loaded = true;
			});
		}
	}
	get companyId() {
		return this._companyId ? this._companyId : '';
	}

	get enableSubmit() {
		return this.orderLines.length > 0 && !this.saving && !this.uploading;
	}

	constructor(
		public readonly productService: ProductService,
		private salesService: SalesService,
		private userService: UserService,
		private language: TranslatePipe,
		private navigation: NavigationService,
		private customDate: CustomDatePipe,
		private taskService: TaskService,
		private notificationService: NotificationService) {

		this.userCredentials = this.userService.getCredentials();
		this.credentials.set('credential.sales', CredentialType.ProductAccessLevelSales);
		this.credentials.set('credential.sales-senior', CredentialType.ProductAccessLevelSalesSenior);
		this.credentials.set('credential.customer-service', CredentialType.ProductAccessLevelCustomerService);

		this.productService.getAllProductFilters().then(filters => this.filters = filters);
		this.productService.getAllVariantLists().then(variantLists => {
			variantLists.forEach(variantList =>
				variantList.variantListOptions.sort((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true }))
			);

			this.variantLists = variantLists;
		});

		this.productService.getAllActiveProducts().then(products => {
			this.products = products.sort((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true }));
		});

		for (let i = this.minQuantity; i <= this.maxQuantity; i++) {
			this.quantityList.push(new SelectListItemModel(String(i), String(i), i === this.minQuantity));
		}
	}

	showSaleAgreement(agreement: SaleAgreementModel) {
		this.navigation.openPopup(NotificationPopupComponent).then(view => {
			view.title = agreement.title;
			view.text = agreement.comment;
		});
	}

	getSaleAgreements() {
		this.salesService.getCompanySaleAgreements(this.companyId, this.userService.getCurrentUser().id).then(agreements => {
			for (let agreement of agreements) {
				if (agreement.orders.length === 0) {
					agreement.title = this.getSaleAgreementTitle(agreement);
					this.saleAgreements.push(agreement);
				}
			}
		});
	}

	getSaleAgreementTitle(agreement: SaleAgreementModel): string {
		let userInitials = agreement.creatorId
			? this.userService.getUserInitialsByUserId(agreement.creatorId)
			: this.language.transform('general.na');

		return this.language.transform('sale.basket-special-agreement') + ' ' +
			userInitials + ' ' + 
			this.customDate.transform(agreement.created, 'shortDateText');
	}

	selectSpecialAgreement(agreement: SaleAgreementModel) {
		if (this.selectedAgreement === agreement)
			this.selectedAgreement = undefined;
		else
			this.selectedAgreement = agreement;
	}

	selectFilter(filter: ProductTagFilterModel) {
		this.selectedFilter = filter;
		this.activeProduct = null;
		this.selectedVariantListOptions = [];
	}

	productSelectedByFilter(product: ProductModel): boolean {
		for (let tag of product.productTags) {
			if (this.selectedFilter != null && tag.tag === this.selectedFilter.tag) return true;
		}
		return false;
	}

	verifyProductCredentials(product: ProductModel): boolean {
		var productCredentialTags = product.productTags.filter(tag => tag.tag.startsWith('credential.'));
		if (productCredentialTags.length == 0) return true; //no required credentials

		return productCredentialTags.some(productTag => {
			if (this.userCredentials.some(uc => uc == this.credentials.get(productTag.tag)))
				return true;
			return false;
		});
	}

	showVariantList(variantList: VariantListModel, product: ProductModel): boolean {
		let variantListUsedByProduct: boolean = false;
		const productVariantLists: any = [];
		const productVariantListsSorted: any = [];
		// Find used variantLists
		for (let variant of product.productVariants) {
			for (let option of variant.productVariantsOptions) {
				if (productVariantLists.indexOf(option.variantOption!.variantListId) === -1) productVariantLists.push(option.variantOption!.variantListId);
				if (option.variantOption!.variantListId === variantList.id) {
					variantListUsedByProduct = true;
				}
			}
			if (variantListUsedByProduct) break;
		}

		// Add used variantLists in same sorting as general variantLists
		for (let list of this.variantLists) {
			if (productVariantLists.indexOf(list.id) !== -1) productVariantListsSorted.push(list.id);
		}

		// Check if there is selected a value in the previous variantList shown
		let selectedValueInPreviousList: boolean = false;
		for (let i = 0; i < productVariantListsSorted.length; i++) {
			if (i === 0 && productVariantListsSorted[i] === variantList.id) {
				selectedValueInPreviousList = true;
				break;
			}
			else if (this.activeProduct === product && productVariantListsSorted[i] === variantList.id) {
				for (let option of this.selectedVariantListOptions) {
					if (productVariantListsSorted[i - 1] === option.variantListId) {
						selectedValueInPreviousList = true;
						break;
					}
				}
			}
		}
		if (variantListUsedByProduct && selectedValueInPreviousList) {
			return true;
		}
		return false;
	}

	variantListOptionUsedByProduct(variantListOption: VariantListOptionModel, product: ProductModel): boolean {
		for (let variant of product.productVariants) {
			for (let option of variant.productVariantsOptions) {
				if (option.variantOption!.id === variantListOption.id) return true;
			}
		}
		return false;
	}

	selectVariant(product: ProductModel, variantListOption?: VariantListOptionModel) {
		if (this.activeProduct !== product) {
			this.quantitySelected = product.groupByVariantAndParentOnOrder ? this.minQuantity : 1;
			this.activeProduct = product;
			this.selectedVariantListOptions = [];
		}
		if (variantListOption) {
			let addVariantListOption = true;
			for (let i in this.selectedVariantListOptions) {
				if (this.selectedVariantListOptions[i].variantListId === variantListOption.variantListId) {
					this.selectedVariantListOptions[i] = variantListOption;
					addVariantListOption = false;
					break;
				}
			}
			if (addVariantListOption) this.selectedVariantListOptions.push(variantListOption);
		}
		this.checkSelectedVariant();
	}

	private checkSelectedVariant() {
		let result: ProductVariantModel | null = null;
		if (this.activeProduct === null) return;
		if (this.activeProduct.productVariants.length === 1) {
			this.selectedVariant = this.activeProduct.productVariants[0];
			this.selectedVariantPeriod = 0;
		} else {
			for (let variant of this.activeProduct.productVariants) {
				result = variant;
				for (let option of variant.productVariantsOptions) {
					let foundOption = false;
					for (let selectedOption of this.selectedVariantListOptions) {
						if (selectedOption.id === option.variantOption!.id) {
							foundOption = true;
							break;
						}
					}
					if (!foundOption) {
						result = null;
					};
				}
				if (result != null) break;
			}
			if (this.selectedVariant == null || result == null || this.selectedVariant.id !== result.id) {
				this.selectedVariant = result;
				this.selectedVariantPeriod = 0;
				if (this.selectedVariant !== null) {
					optionLoop: for (let option of this.selectedVariant.productVariantsOptions) {
						for (let variantList of this.variantLists.filter(variantList => variantList.type === VariantListTypes.Period)) {
							if (option.variantOption!.variantListId === variantList.id) {
								this.selectedVariantPeriod = Number(option.variantOption!.name);
								break optionLoop;
							}
						}
					}
				}
			}
		}
		if (this.selectedVariant != null) {
			this.price = this.selectedVariant.price;
			this.updateMonthlyPrice();
			this.updateDiscount();
		}
	}

	updatePrice(price: number, source: string) {
		if (source === 'total') {
			this.price = price;
			this.updateMonthlyPrice();
		} else if (source === 'month') {
			this.monthPrice = price;
			this.price = this.monthPrice * this.selectedVariantPeriod;
		}
		this.updateDiscount();
	}

	updateMonthlyPrice() {
		this.monthPrice = 0;
		if (this.price > 0 && this.selectedVariantPeriod > 0) {
			this.monthPrice = this.price / this.selectedVariantPeriod;
		}
	}

	updateDiscount() {
		this.discount = '0';
		if (this.selectedVariant && this.price < this.selectedVariant.price) {
			this.discount = Intl.NumberFormat('da-dk', { maximumFractionDigits: 0 }).format(100 - (this.price / this.selectedVariant.price * 100));
		}
	}

	variantListOptionSelected(variantListOption: VariantListOptionModel): boolean {
		for (let option of this.selectedVariantListOptions) {
			if (option.id === variantListOption.id) return true;
		}
		return false;
	}

	addVariantToOrder() {
		if (!this.selectedVariant || !this.activeProduct) return;

		let newOrderLine = new OrderLineModel();
		newOrderLine.productVariant = this.selectedVariant;
		newOrderLine.quantity = 1;
		newOrderLine.totalPrice = +this.price;

		if (this.activeProduct.requiredProduct) {

			// set potential picking product data
			this.pickingParentProduct = {
				targetProduct: this.activeProduct.requiredProduct!,
				pendingOrderLine: newOrderLine
			};

			const matchingCompanyProducts = this.potentialParentProductsOnCompany(this.activeProduct)
				.filter(product => this.selectableParent(product));
			const matchingBasketProducts = this.potentialParentProductsInBasket(this.activeProduct)
				.filter(product => this.selectableParent(product));

			// If only one matching product exists, and it is in the basket, just add to it automatically
			if (matchingCompanyProducts.length == 0 && matchingBasketProducts.length == 1) {
				this.clickedOrderLine(matchingBasketProducts[0]);
			}

			if (matchingCompanyProducts.length === 0 && matchingBasketProducts.length === 0)
				this.pickingParentProduct = undefined;

		} else {
			let orderLines: OrderLineModel[] = [];
			for (let i = 0; i < this.quantitySelected; i++) {
				const orderLine = Object.assign(new OrderLineModel(), newOrderLine);
				orderLines.push(orderLine);
			}
			this.addOrderLineToOrderLines(orderLines);
		}
	}

	clickedOrderLine(orderLine: OrderLineModel) {
		if (!this.pickingParentProduct || !this.selectableParent(orderLine)) return;

		let orderLines: OrderLineModel[] = [];
		for (var i = 0; i < this.quantitySelected; i++) {
			const newOrderLine = Object.assign(new OrderLineModel(), this.pickingParentProduct.pendingOrderLine);
			newOrderLine.parentLine = orderLine;
			orderLine.childLines.push(newOrderLine);
			orderLines.push(newOrderLine);
		}
		this.addOrderLineToOrderLines(orderLines);
	}

	addOrderLineToOrderLines(orderLines: OrderLineModel[]) {
		orderLines.forEach((orderLine: OrderLineModel) => {
			this.orderLines.push(orderLine);
		});
		this.calculateOrderLineTotal();
		this.activeProduct = null;
		this.pickingParentProduct = undefined;
	}

	clickedOutside(targetElement: EventTarget | null) {
		if (this.basketArea && (!(targetElement instanceof Node) || !this.basketArea.nativeElement.contains(targetElement))) this.pickingParentProduct = undefined;
	}

	removeOrderLineFromOrder(orderLine: OrderLineModel) {
		if (orderLine.childLines) {
			orderLine.childLines.forEach(childLine => {
				this.removeOrderLineFromOrder(childLine);
			});
		}
		if (orderLine.parentLine) {
			const indexToRemove = orderLine.parentLine.childLines.findIndex(childLine => { return childLine === orderLine });
			orderLine.parentLine.childLines.splice(indexToRemove, 1);
		}

		const orderIndex = this.orderLines.findIndex(product => { return product === orderLine });
		if (orderIndex >= 0) {
			this.orderLines.splice(orderIndex, 1);
		}
		this.calculateOrderLineTotal();
	}

	calculateOrderLineTotal() {
		this.orderLineTotal = 0;
		this.orderLines.forEach(product => this.orderLineTotal += product.totalPrice);
		this.parentOrderLines = this.orderLines.filter(product => !product.parentLine);
	}

	potentialParent(orderLine: OrderLineModel, targetId?: string): boolean {
		if (!targetId) targetId = this.pickingParentProduct ? this.pickingParentProduct.targetProduct.id : '';
		if (targetId === '') return false;
		if (orderLine.productVariant!.productId === targetId) {
			return true;
		} else if (orderLine.childLines.length > 0) {
			return orderLine.childLines.some(childLine => this.potentialParent(childLine, targetId));
		}
		return false;
	}

	selectableParent(orderLine: OrderLineModel) {
		if (this.pickingParentProduct && orderLine.productVariant!.productId === this.pickingParentProduct.targetProduct.id) {
			return true;
		}
		return false;
	}

	displayCompanyProduct(orderLine: OrderLineModel): boolean {
		if (orderLine.id != undefined && (this.potentialParent(orderLine) || orderLine.childLines.some(childLine => !childLine.id)))
			return true;
		else if (orderLine.childLines.length > 0) {
			return orderLine.childLines.some(childLine => this.displayCompanyProduct(childLine));
		}
		return false;
	}

	missingRequiredProduct(product: ProductModel) {
		if (!product.requiredProductId) return false;
		if (this.potentialParentProductsOnCompany(product).length > 0) return false;
		if (this.potentialParentProductsInBasket(product).length > 0) return false;

		return true;
	}

	displayEmptyBasket(): boolean {
		if (this.orderLines.length > 0) return false;

		return !this.companyProducts.some((orderLineModel: OrderLineModel) => {
			return this.displayCompanyProduct(orderLineModel);
		});
	}

	potentialParentProductsOnCompany(childLineType: ProductModel): OrderLineModel[] {
		return this.companyProducts.filter(companyProduct => this.potentialParent(companyProduct, childLineType.requiredProductId!));
	}

	potentialParentProductsInBasket(childLineType: ProductModel): OrderLineModel[] {
		return this.orderLines.filter(orderLine => this.potentialParent(orderLine, childLineType.requiredProductId!));
	}
}
